import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { SERVICES } from "../data/site";
import { fadeUp, stagger } from "../lib/animations";

const ServiceBlock = ({ service, reverse }) => (
  <motion.div
    variants={stagger}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, margin: "-100px" }}
    className="grid grid-cols-12 gap-8 lg:gap-12 items-start"
    data-testid={`service-${service.id}`}
  >
    <motion.div
      variants={fadeUp}
      className={`col-span-12 lg:col-span-5 ${reverse ? "lg:order-2" : ""}`}
    >
      <div className="lg:sticky lg:top-28">
        <div className="flex items-baseline gap-4">
          <span className="font-heading font-extrabold text-brand text-2xl">
            {service.index}
          </span>
          <span className="text-xs uppercase tracking-[0.2em] font-semibold text-brand-ink/40">
            {service.tagline}
          </span>
        </div>
        <h3 className="mt-4 font-heading font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-brand-ink leading-[1.02]">
          {service.title}
        </h3>
        <p className="mt-5 text-base sm:text-lg leading-relaxed text-brand-ink/60 max-w-md">
          {service.description}
        </p>
        <div className="mt-7 relative h-56 sm:h-72 overflow-hidden rounded-2xl border border-gray-200 group">
          <img
            src={service.image}
            alt={service.title}
            className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
          />
        </div>
      </div>
    </motion.div>

    <motion.div
      variants={fadeUp}
      className={`col-span-12 lg:col-span-7 ${reverse ? "lg:order-1" : ""}`}
    >
      <div className="grid sm:grid-cols-2 gap-4">
        {service.groups.map((group, gi) => (
          <div
            key={gi}
            className={`rounded-2xl border border-gray-200 bg-white p-6 hover:border-brand hover:shadow-sm transition-[border-color,box-shadow] duration-300 ${
              service.groups.length % 2 !== 0 && gi === service.groups.length - 1
                ? "sm:col-span-2"
                : ""
            }`}
          >
            <h4 className="font-heading font-bold text-lg text-brand-ink">
              {group.name}
            </h4>
            <ul className="mt-4 space-y-2.5">
              {group.items.map((item, ii) => (
                <li
                  key={ii}
                  className="flex items-start gap-2.5 text-sm text-brand-ink/70"
                >
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </motion.div>
  </motion.div>
);

export const Services = () => {
  return (
    <section id="services" className="bg-brand-surface py-24 sm:py-32" data-testid="services-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          className="max-w-3xl"
        >
          <span className="text-xs uppercase tracking-[0.22em] font-semibold text-brand-ink/40">
            What we do
          </span>
          <h2 className="mt-4 font-heading font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl text-brand-ink leading-none">
            Three studios.
            <br />
            One immersive vision.
          </h2>
        </motion.div>

        <div className="mt-16 sm:mt-24 space-y-24 sm:space-y-32">
          {SERVICES.map((service, i) => (
            <ServiceBlock key={service.id} service={service} reverse={i % 2 === 1} />
          ))}
        </div>
      </div>
    </section>
  );
};
